import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import { alpha, keyframes } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LaunchIcon from "@mui/icons-material/Launch";
import GitHubIcon from "@mui/icons-material/GitHub";
import projectData from "../../projects_data";
import { applySeo } from "../../seo";
import ProjectVisual from "./ProjectVisual";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const tagColors = {
  Web3: { bg: "rgba(147, 51, 234, 0.15)", color: "#a855f7" },
  "Full-Stack": { bg: "rgba(0, 127, 255, 0.15)", color: "#007FFF" },
  MUI: { bg: "rgba(0, 212, 170, 0.15)", color: "#00D4AA" },
  DeFi: { bg: "rgba(255, 159, 64, 0.15)", color: "#ff9f40" },
  SaaS: { bg: "rgba(236, 72, 153, 0.15)", color: "#ec4899" },
  "Real-time": { bg: "rgba(34, 197, 94, 0.15)", color: "#22c55e" },
  "Oracle Fusion": { bg: "rgba(0, 127, 255, 0.15)", color: "#4DA3FF" },
  Invoicing: { bg: "rgba(0, 212, 170, 0.15)", color: "#00D4AA" },
  Enterprise: { bg: "rgba(99, 102, 241, 0.15)", color: "#6366f1" },
  Accounting: { bg: "rgba(34, 197, 94, 0.15)", color: "#22c55e" },
  GL: { bg: "rgba(245, 158, 11, 0.15)", color: "#f59e0b" },
  Reports: { bg: "rgba(59, 130, 246, 0.15)", color: "#3b82f6" },
  Dashboard: { bg: "rgba(236, 72, 153, 0.15)", color: "#ec4899" },
  React: { bg: "rgba(0, 127, 255, 0.15)", color: "#4DA3FF" },
  Wallets: { bg: "rgba(147, 51, 234, 0.15)", color: "#a855f7" },
  DApp: { bg: "rgba(147, 51, 234, 0.15)", color: "#a855f7" },
};

function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const project = projectData.find((proj) => proj.id === id);

  React.useEffect(() => {
    if (project) {
      applySeo({
        title: `${project.title} | Rahman Idrees Case Study`,
        description: `${project.title}: ${project.description.substring(0, 155)}...`,
        keywords: `${project.title}, ${project.tags?.join(", ")}, Rahman Idrees case study, MERN stack, enterprise SaaS, Web3`,
        image: project.images?.[0],
        path: `/project/${project.id}`,
        type: "article",
      });
    }
  }, [project]);

  if (!project) {
    return (
      <Container maxWidth="lg" sx={{ py: 20, textAlign: "center" }}>
        <Typography variant="h4" component="h1" color="error">
          Project not found!
        </Typography>
        <Button
          onClick={() => navigate("/project")}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Back to Projects
        </Button>
      </Container>
    );
  }

  const getTagStyle = (tag) => {
    return tagColors[tag] || { bg: "rgba(0, 127, 255, 0.15)", color: "#007FFF" };
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: { xs: 10, md: 12 },
        position: "relative",
      }}
    >
      {/* Background accent */}
      <Box
        sx={{
          position: "absolute",
          top: "20%",
          left: "-10%",
          width: "30%",
          height: "30%",
          background: "radial-gradient(circle, rgba(0, 127, 255, 0.1) 0%, transparent 70%)",
          filter: "blur(60px)",
          pointerEvents: "none",
        }}
      />

      <Container maxWidth="lg">
        {/* Back Button */}
        <Button
          onClick={() => navigate(-1)}
          startIcon={<ArrowBackIcon />}
          sx={{
            mb: 4,
            color: "text.secondary",
            "&:hover": {
              color: "primary.main",
            },
          }}
        >
          Back to Projects
        </Button>

        <Grid container spacing={6}>
          {/* Image Gallery */}
          <Grid item xs={12} md={7}>
            <Paper
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                position: "relative",
                animation: `${fadeIn} 0.6s ease-out`,
              }}
            >
              <ProjectVisual
                project={project}
                height={{ xs: 300, md: 400 }}
                variant="detail"
              />
            </Paper>
          </Grid>

          {/* Project Info */}
          <Grid item xs={12} md={5}>
            <Box
              sx={{
                animation: `${fadeIn} 0.6s ease-out`,
                animationDelay: "0.2s",
                animationFillMode: "both",
              }}
            >
              {/* Tags */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                {project.tags?.map((tag) => {
                  const style = getTagStyle(tag);
                  return (
                    <Chip
                      key={tag}
                      label={tag}
                      size="small"
                      sx={{
                        backgroundColor: style.bg,
                        color: style.color,
                        fontWeight: 600,
                      }}
                    />
                  );
                })}
              </Box>

              {/* Title */}
              <Typography variant="h3" component="h1" fontWeight={700} sx={{ mb: 1 }}>
                {project.title}
              </Typography>

              {/* <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {project.year}
              </Typography> */}

              {/* Description */}
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ mb: 4, lineHeight: 1.8 }}
              >
                {project.description}
              </Typography>

              {/* Technologies */}
              <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2 }}>
                Technologies Used
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}>
                {project.technologies?.map((tech) => (
                  <Chip
                    key={tech}
                    label={tech}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: (theme) => alpha(theme.palette.divider, 0.3),
                    }}
                  />
                ))}
              </Box>

              {project.outcomes?.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" component="h2" fontWeight={600} sx={{ mb: 2 }}>
                    Case Study Highlights
                  </Typography>
                  <Box
                    component="ul"
                    sx={{
                      m: 0,
                      pl: 2.5,
                      color: "text.secondary",
                      "& li": {
                        mb: 1,
                        lineHeight: 1.7,
                      },
                    }}
                  >
                    {project.outcomes.map((outcome) => (
                      <Typography key={outcome} component="li" variant="body2">
                        {outcome}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ display: "flex", gap: 2 }}>
                {project.demoLink && (
                  <Button
                    component="a"
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    endIcon={<LaunchIcon />}
                    aria-label={`Open live demo for ${project.title}`}
                  >
                    Live Demo
                  </Button>
                )}
                {project.ghLink && (
                  <Button
                    component="a"
                    href={project.ghLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="outlined"
                    startIcon={<GitHubIcon />}
                    aria-label={`Open source code for ${project.title}`}
                  >
                    View Code
                  </Button>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default ProjectDetail;
